import React from 'react';
import type { Task } from '../services/api';
import { formatFinanceCell, formatSharePercent } from '../utils/taskFinance';
import {
  BUDGET_GROUP_LABELS,
  HeaderMergeCell,
  MarginCalcTableData,
} from '../utils/marginCalc';
import { displayContractSumDraft } from '../utils/table';

interface MarginCalcTableProps {
  columnTasks: Task[];
  headerCells: {
    contracts: HeaderMergeCell[];
    taskIds: HeaderMergeCell[];
    workTypes: HeaderMergeCell[];
  };
  tableData: MarginCalcTableData;
  editable?: boolean;
  getDraftValue?: (contractsTaskId: number, financeKey: keyof Task) => string;
  onDraftChange?: (contractsTaskId: number, financeKey: keyof Task, value: string) => void;
}

const MarginCalcTable: React.FC<MarginCalcTableProps> = ({
  columnTasks,
  headerCells,
  tableData,
  editable = false,
  getDraftValue,
  onDraftChange,
}) => {
  const amountRow = tableData.rowSums.find((item) => item.row.key === 'amount');
  const costRows = tableData.rowSums.filter((item) => item.row.budgetGroup === 'cost');
  const marginRows = tableData.rowSums.filter((item) => item.row.budgetGroup === 'margin');

  const renderGroupTotalRow = (
    key: string,
    label: string,
    valuesByTaskId: Map<number, number | null>,
    rowSum: number,
  ) => (
    <tr key={key} className="task-detail__budget-section-row">
      <th scope="row">{label}</th>
      {columnTasks.map((columnTask) => (
        <td key={columnTask.contracts_task_id} className="task-detail__budget-task-cell">
          {formatFinanceCell(valuesByTaskId.get(columnTask.contracts_task_id) ?? null)}
        </td>
      ))}
      <td className="task-detail__budget-group-sum">{formatFinanceCell(rowSum)}</td>
      <td className="task-detail__budget-percent-cell">
        {formatSharePercent(rowSum, tableData.amountRowSum)}
      </td>
    </tr>
  );

  const renderDetailRow = ({ row, valuesByTaskId, rowSum }: (typeof tableData.rowSums)[number]) => {
    const financeKey = row.draftKey;
    const isComputedRow = row.computed === true;

    return (
      <tr key={row.key} className="task-detail__budget-detail-row">
        <th scope="row" className="task-detail__budget-detail-label">
          {row.label}
        </th>
        {columnTasks.map((columnTask) => {
          const cellValue = valuesByTaskId.get(columnTask.contracts_task_id) ?? null;
          const draftStr =
            editable && getDraftValue && financeKey
              ? getDraftValue(columnTask.contracts_task_id, financeKey)
              : '';

          return (
            <td key={columnTask.contracts_task_id} className="task-detail__budget-task-cell">
              {editable && onDraftChange && financeKey && !isComputedRow ? (
                <input
                  type="text"
                  inputMode="numeric"
                  className="contractor-detail__field task-detail__cell-input task-detail__cell-input--num"
                  value={displayContractSumDraft(draftStr)}
                  onChange={(e) => onDraftChange(columnTask.contracts_task_id, financeKey, e.target.value)}
                />
              ) : (
                formatFinanceCell(cellValue)
              )}
            </td>
          );
        })}
        <td className="task-detail__budget-group-sum">{formatFinanceCell(rowSum)}</td>
        <td className="task-detail__budget-percent-cell">
          {formatSharePercent(rowSum, tableData.amountRowSum)}
        </td>
      </tr>
    );
  };

  return (
    <div className="task-detail__budget-scroll">
      <table className="task-detail__mini-table task-detail__mini-table--compact task-detail__mini-table--budget task-detail__mini-table--budget-grid task-detail__mini-table--margin-calc">
        <colgroup>
          <col className="task-detail__mini-table-col-label" />
          {columnTasks.map((columnTask) => (
            <col key={columnTask.contracts_task_id} className="task-detail__mini-table-col-value" />
          ))}
          <col className="task-detail__mini-table-col-value task-detail__mini-table-col-group-sum" />
          <col className="task-detail__mini-table-col-value task-detail__mini-table-col-percent" />
        </colgroup>
        <thead>
          <tr className="task-detail__budget-head-row task-detail__budget-head-row--level-1">
            <th className="task-detail__budget-level-head">Договор</th>
            {headerCells.contracts.map((cell, index) => (
              <th
                key={`contract-${index}`}
                colSpan={cell.colSpan}
                className="task-detail__budget-col-head task-detail__budget-col-head--contract"
              >
                {cell.value}
              </th>
            ))}
            <th rowSpan={3} className="task-detail__budget-col-head task-detail__budget-sum-head">
              Итого по ID
            </th>
            <th rowSpan={3} className="task-detail__budget-col-head task-detail__budget-percent-head">
              %
            </th>
          </tr>
          <tr className="task-detail__budget-head-row task-detail__budget-head-row--level-2">
            <th className="task-detail__budget-level-head">Задача</th>
            {headerCells.taskIds.map((cell, index) => (
              <th
                key={`task-id-${index}`}
                colSpan={cell.colSpan}
                className="task-detail__budget-col-head task-detail__budget-col-head--task-id"
              >
                {cell.value}
              </th>
            ))}
          </tr>
          <tr className="task-detail__budget-head-row task-detail__budget-head-row--level-3">
            <th className="task-detail__budget-level-head">Вид работ</th>
            {headerCells.workTypes.map((cell, index) => (
              <th
                key={`work-type-${index}`}
                colSpan={cell.colSpan}
                className="task-detail__budget-col-head task-detail__budget-col-head--work-type"
              >
                {cell.value}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {costRows.length > 0
            ? renderGroupTotalRow(
                'group-cost',
                BUDGET_GROUP_LABELS.cost,
                tableData.groupSums.cost.valuesByTaskId,
                tableData.groupSums.cost.rowSum,
              )
            : null}
          {costRows.map((item) => renderDetailRow(item))}
          {marginRows.length > 0
            ? renderGroupTotalRow(
                'group-margin',
                BUDGET_GROUP_LABELS.margin,
                tableData.groupSums.margin.valuesByTaskId,
                tableData.groupSums.margin.rowSum,
              )
            : null}
          {marginRows.map((item) => renderDetailRow(item))}
          {amountRow ? (
            <tr key="amount-total" className="task-detail__budget-section-row">
              <th scope="row">СУММА</th>
              {columnTasks.map((columnTask) => {
                const cellValue = amountRow.valuesByTaskId.get(columnTask.contracts_task_id) ?? null;
                const draftStr =
                  editable && getDraftValue ? getDraftValue(columnTask.contracts_task_id, 'amount') : '';

                return (
                  <td key={columnTask.contracts_task_id} className="task-detail__budget-task-cell">
                    {editable && onDraftChange ? (
                      <input
                        type="text"
                        inputMode="numeric"
                        className="contractor-detail__field task-detail__cell-input task-detail__cell-input--num"
                        value={displayContractSumDraft(draftStr)}
                        onChange={(e) => onDraftChange(columnTask.contracts_task_id, 'amount', e.target.value)}
                      />
                    ) : (
                      formatFinanceCell(cellValue)
                    )}
                  </td>
                );
              })}
              <td className="task-detail__budget-group-sum">{formatFinanceCell(amountRow.rowSum)}</td>
              <td className="task-detail__budget-percent-cell">
                {formatSharePercent(amountRow.rowSum, tableData.amountRowSum)}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
};

export default MarginCalcTable;
